import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchool } from '../utils/activeSchool';
import { syncPendingPayments } from '../utils/paymentSync';
import { formatDate } from '../utils/dateFormat';

const API = getApiBaseUrl();

function assetUrl(path) {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = API.replace(/\/api\/?$/, '');
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

export default function RentalPage() {
    const { t } = useTranslation(['rentalPage', 'common']);
    const active = getActiveSchool();
    const schoolId = active?.schoolId;
    const studentId = active?.studentId;

    const [items, setItems] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [cart, setCart] = useState({});
    const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState('');
    const [tab, setTab] = useState('catalog');

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    useEffect(() => { if (schoolId) load(); }, [schoolId]);

    async function load() {
        setLoading(true);
        try {
            await syncPendingPayments(api, schoolId);
            const [catRes, bookRes] = await Promise.all([
                api.get('/inventory', { params: { schoolId, type: 'rent' } }),
                api.get('/inventory/my-bookings', { params: { schoolId } })
            ]);
            setItems(catRes.data || []);
            setBookings(bookRes.data || []);
        } catch {
            setError(t('rentalPage:loadError'));
        }
        setLoading(false);
    }

    function toggleCart(id) {
        setCart(prev => {
            const next = { ...prev };
            if (next[id]) delete next[id];
            else next[id] = 1;
            return next;
        });
    }

    function setQty(id, q) {
        setCart(prev => ({ ...prev, [id]: Math.max(1, parseInt(q, 10) || 1) }));
    }

    const lines = Object.entries(cart).map(([id, qty]) => ({
        inventoryItemId: parseInt(id, 10),
        quantity: qty
    }));

    const total = lines.reduce((sum, l) => {
        const item = items.find(i => i.id === l.inventoryItemId);
        return sum + (item ? item.price * l.quantity : 0);
    }, 0);

    async function checkout(payMethod) {
        if (!schoolId || lines.length === 0) return;
        setPaying(true);
        setError('');
        try {
            const res = await api.post('/inventory/checkout', {
                schoolId,
                studentId,
                bookingDate,
                lines,
                payMethod
            });
            if (res.data.confirmationUrl) {
                window.open(res.data.confirmationUrl, '_blank');
                alert(t('rentalPage:payTabHint'));
                setTimeout(() => load(), 5000);
            } else {
                alert(res.data.message || t('rentalPage:rentSuccess'));
                setCart({});
                load();
            }
        } catch (err) {
            const d = err.response?.data;
            if (d?.error === 'Insufficient balance') {
                setError(t('rentalPage:insufficientBalance', { required: d.required, balance: d.currentBalance }));
            } else {
                setError(d?.message || t('rentalPage:checkoutError'));
            }
        }
        setPaying(false);
    }

    if (!schoolId) {
        return (
            <OwnerPageLayout title={t('rentalPage:title')} maxWidth={720}>
                <p style={{ textAlign: 'center' }}>{t('rentalPage:noSchoolHint')}</p>
                <p style={{ textAlign: 'center' }}><a href="/apply" style={{ color: '#EB3333' }}>{t('rentalPage:applyLink')}</a></p>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('rentalPage:title')}
            subtitle={t('rentalPage:subtitle', { school: active?.schoolName || t('rentalPage:schoolFallback') })}
            maxWidth={720}
        >

                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                    {['catalog', 'orders'].map(tabId => (
                        <button key={tabId} type="button" onClick={() => setTab(tabId)} style={{
                            padding: '10px 18px', borderRadius: '10px', border: 'none',
                            background: tab === tabId ? '#EB3333' : 'var(--sp-gray-100)',
                            color: tab === tabId ? '#fff' : '#333', fontWeight: '600', cursor: 'pointer'
                        }}>
                            {tabId === 'catalog' ? t('rentalPage:tabCatalog') : t('rentalPage:tabOrders')}
                        </button>
                    ))}
                </div>

                {tab === 'catalog' && (
                    <>
                        <label style={{ fontSize: '13px', color: '#64748b' }}>{t('rentalPage:lessonDate')}</label>
                        <input type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)}
                            style={{ display: 'block', marginBottom: '16px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />

                        {loading ? <p>{t('common:loading')}</p> : items.length === 0 ? (
                            <p style={{ color: '#888' }}>{t('rentalPage:emptyCatalog')}</p>
                        ) : (
                            <div style={{ display: 'grid', gap: '12px' }}>
                                {items.map(item => (
                                    <div key={item.id} style={{
                                        background: '#fff', borderRadius: '12px', padding: '14px',
                                        border: cart[item.id] ? '2px solid #EB3333' : '1px solid var(--sp-gray-100)',
                                        display: 'flex', gap: '14px'
                                    }}>
                                        <div style={{ width: '64px', height: '64px', borderRadius: '8px', background: '#f0ebff', overflow: 'hidden' }}>
                                            {item.imageUrl ? (
                                                <img src={assetUrl(item.imageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : <span style={{ fontSize: '28px', lineHeight: '64px', display: 'block', textAlign: 'center' }}>🛼</span>}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '700' }}>{item.name}</div>
                                            {item.characteristics && <div style={{ fontSize: '12px', color: '#64748b' }}>{item.characteristics}</div>}
                                            <div style={{ marginTop: '4px' }}>{t('rentalPage:priceAvailable', { price: item.price, qty: item.availableQuantity })}</div>
                                        </div>
                                        {cart[item.id] ? (
                                            <input type="number" min="1" max={item.availableQuantity} value={cart[item.id]}
                                                onChange={e => setQty(item.id, e.target.value)}
                                                style={{ width: '48px', padding: '6px', borderRadius: '6px', border: '1px solid #ddd' }} />
                                        ) : (
                                            <button type="button" onClick={() => toggleCart(item.id)}
                                                disabled={item.availableQuantity < 1}
                                                style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#EB3333', color: '#fff', cursor: 'pointer' }}>
                                                +
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {lines.length > 0 && (
                            <div style={{
                                marginTop: '20px', background: '#fff', padding: '16px', borderRadius: '12px',
                                border: '1px solid var(--sp-gray-100)'
                            }}>
                                <div style={{ fontWeight: '700', marginBottom: '12px' }}>{t('rentalPage:total', { amount: total.toLocaleString() })}</div>
                                {error && <p style={{ color: '#c62828', fontSize: '13px' }}>{error}</p>}
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <button type="button" disabled={paying} onClick={() => checkout('balance')}
                                        style={payBtn}>{t('rentalPage:payBalance')}</button>
                                    <button type="button" disabled={paying} onClick={() => checkout('card')}
                                        style={{ ...payBtn, background: '#1A1A1A' }}>{t('rentalPage:payCard')}</button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {tab === 'orders' && (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {bookings.length === 0 ? <p style={{ color: '#888' }}>{t('rentalPage:noOrders')}</p> : bookings.map(b => (
                            <div key={`${b.kind || 'booking'}-${b.id}`} style={{
                                background: '#fff', padding: '14px', borderRadius: '10px',
                                border: b.kind === 'pending_order' ? '1px solid #fde68a' : '1px solid var(--sp-gray-100)'
                            }}>
                                <div style={{ fontWeight: '600' }}>
                                    {b.itemName}{b.quantity > 0 ? ` × ${b.quantity}` : ''}
                                </div>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>
                                    {formatDate(b.bookingDate)} · {b.totalPrice} ₽
                                    {b.kind === 'pending_order'
                                        ? t('rentalPage:pendingCardPayment')
                                        : ` · ${b.status}`}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
        </OwnerPageLayout>
    );
}

const payBtn = {
    padding: '12px 20px', borderRadius: '10px', border: 'none',
    background: '#EB3333', color: '#fff', fontWeight: '700', cursor: 'pointer'
};
